import React from 'react';
import { Card, Tag } from 'antd';
import { Link } from 'react-router-dom';
import type { AuditLogEntry } from '../services/adminService';

/**
 * AuditLogCard
 * ----------------------------------------------------------------------------
 * Renders a single AuditLogEntry as a card for narrow viewports.
 *
 * The entire card is clickable — tapping it opens the detail Drawer, matching
 * the desktop table's onRow click behaviour. The onRowClick handler is passed
 * in from AuditLog.tsx (same handler that drives the Drawer).
 *
 * Read-only: no kebab menu, no actions.
 */

const actionTagColor = (action: string): string => {
  if (action.startsWith('create') || action === 'allocate') return 'green';
  if (action.startsWith('update') || action === 'assign') return 'blue';
  if (action.startsWith('delete') || action === 'deallocate' || action === 'unassign') return 'red';
  if (action === 'login') return 'purple';
  return 'default';
};

const formatTimestamp = (val: string): string => {
  const d = new Date(val);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' ' +
    d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })
  );
};

const renderResourceLink = (
  resourceType: string | null,
  resourceId: string | null
): React.ReactNode => {
  if (!resourceType || !resourceId) return '\u2014';
  const label = `${resourceType} #${resourceId}`;
  if (resourceType === 'organization') {
    return <Link to={`/admin/organizations/${resourceId}`}>{label}</Link>;
  }
  if (resourceType === 'user') {
    return <Link to="/admin/users">{label}</Link>;
  }
  return label;
};

const FieldRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ display: 'flex', gap: 8, padding: '3px 0', fontSize: 13 }}>
    <span style={{ color: 'rgba(0,0,0,0.55)', minWidth: 80, flexShrink: 0 }}>{label}</span>
    <span style={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</span>
  </div>
);

type Props = {
  entry: AuditLogEntry;
  onRowClick: (entry: AuditLogEntry) => void;
};

const AuditLogCard: React.FC<Props> = ({ entry, onRowClick }) => (
  <Card
    size="small"
    style={{ marginBottom: 8, cursor: 'pointer' }}
    styles={{ body: { padding: 12 } }}
    onClick={() => onRowClick(entry)}
    hoverable
  >
    {/* Timestamp prominently at top, action tag inline */}
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 8,
      }}
    >
      <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.55)' }}>
        {formatTimestamp(entry.created_at)}
      </span>
      <Tag color={actionTagColor(entry.action)} style={{ margin: 0 }}>
        {entry.action}
      </Tag>
    </div>
    <FieldRow label="User">
      {entry.user_email ? (
        <Link to="/admin/users" onClick={e => e.stopPropagation()}>
          {entry.user_email}
        </Link>
      ) : (
        '\u2014'
      )}
    </FieldRow>
    <FieldRow label="Resource">
      <span onClick={e => e.stopPropagation()}>
        {renderResourceLink(entry.resource_type, entry.resource_id)}
      </span>
    </FieldRow>
  </Card>
);

export default AuditLogCard;
